// VFR Devis Digital - Configurator Store
// Story E1-S2: État du configurateur produit

import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { catalogueService } from '@/services/catalogueService'
import type {
  CatalogProduct,
  CatalogCategoryId,
  ProductConfiguration,
  CalculatedPrice
} from '@/types/catalogue'

// Story 9.3: Interface pour les données transmises au devis
export interface QuoteItemFromConfig {
  productId: string
  productName: string
  categoryId: string
  description: string  // Déclinaisons formatées
  quantity: number
  unitPrice: number    // Prix unitaire HT
  totalPrice: number   // Prix total HT
  configuration: ProductConfiguration
}

export const useConfiguratorStore = defineStore('configurator', () => {
  // ============================================
  // State
  // ============================================

  const isOpen = ref(false)
  const product = ref<CatalogProduct | null>(null)
  const categoryId = ref<CatalogCategoryId | null>(null)
  const configuration = ref<ProductConfiguration | null>(null)
  const calculatedPrice = ref<CalculatedPrice | null>(null)
  const loading = ref(false)
  const manualPrice = ref<number | null>(null)

  // Story 9.3: Callback pour l'ajout au devis
  const onAddToQuoteCallback = ref<((data: QuoteItemFromConfig) => void) | null>(null)

  // Edit mode: true when re-editing an existing catalogue line
  const isEditMode = ref(false)

  // ============================================
  // Getters
  // ============================================

  const productType = computed(() => product.value?.type || null)

  const categoryName = computed(() => {
    if (!categoryId.value) return ''
    const category = catalogueService.getCategoryById(categoryId.value)
    return category?.name || categoryId.value.toUpperCase()
  })

  const breadcrumb = computed(() => {
    if (!product.value || !categoryId.value) return []

    return [
      { label: 'CATALOGUE', path: '/catalogue' },
      { label: categoryName.value, path: null },
      { label: product.value.name, path: null }
    ]
  })

  const declinationsWithDefaults = computed(() => {
    if (!product.value) return []
    return product.value.declinations.map(dec => ({
      ...dec,
      currentValue: configuration.value?.declinations[dec.id] ?? dec.default
    }))
  })

  // Option visibility: tracks which options are visible per declination (restriction system)
  const optionVisibility = ref<Record<string, string[]>>({})

  // Visible declinations: excludes hidden fields (internal/machine options)
  // Hidden declinations still exist in configuration.declinations for price calculation
  const visibleDeclinations = computed(() => {
    return declinationsWithDefaults.value.filter(dec => !dec.hidden)
  })

  const quantity = computed(() => configuration.value?.quantity ?? 1)

  const dimensions = computed(() => configuration.value?.dimensions ?? {
    largeur: 100,
    hauteur: 100
  })

  // ============================================
  // Actions
  // ============================================

  function openConfigurator(selectedProduct: CatalogProduct, catId: CatalogCategoryId) {
    product.value = selectedProduct
    categoryId.value = catId
    isOpen.value = true
    isEditMode.value = false

    // Initialize configuration with defaults
    const defaultConfig = catalogueService.getDefaultConfiguration(selectedProduct.id)
    if (defaultConfig) {
      configuration.value = defaultConfig
      computeVisibility()
      autoSelectBroderieTranches()
      recalculatePrice()
    }

    console.log('[ConfiguratorStore] Opened:', selectedProduct.name, 'type:', selectedProduct.type)
  }

  function openForEdit(selectedProduct: CatalogProduct, catId: CatalogCategoryId, existingConfig: ProductConfiguration) {
    product.value = selectedProduct
    categoryId.value = catId
    isOpen.value = true
    isEditMode.value = true

    // Use existing config instead of defaults
    configuration.value = { ...existingConfig }
    recalculatePrice()

    console.log('[ConfiguratorStore] Opened for edit:', selectedProduct.name)
  }

  function closeConfigurator() {
    isOpen.value = false
    // Keep product data for potential reopen
    console.log('[ConfiguratorStore] Closed')
  }

  function resetConfigurator() {
    isOpen.value = false
    isEditMode.value = false
    product.value = null
    categoryId.value = null
    configuration.value = null
    calculatedPrice.value = null
    manualPrice.value = null
  }

  function setManualPrice(price: number) {
    manualPrice.value = price > 0 ? price : null
  }

  function computeVisibility() {
    if (!product.value || !configuration.value) {
      optionVisibility.value = {}
      return
    }

    const restrictions = product.value.restrictionIndex
    if (!restrictions || Object.keys(restrictions).length === 0) {
      optionVisibility.value = {}
      return
    }

    // Start: all options visible
    const hidden = new Set<string>() // "decSlug:optionName" keys to hide

    // Apply restrictions from visible (non-hidden) selections only
    // Hidden declinations (internal tarifs, machines) must not filter user-facing options
    for (const dec of product.value.declinations) {
      if (dec.hidden) continue

      const selected = configuration.value.declinations[dec.id]
      if (!selected || typeof selected !== 'string') continue

      const key = `${dec.id}:${selected}`
      const targets = restrictions[key]
      if (!targets) continue

      for (const t of targets) {
        hidden.add(`${t.targetDecId}:${t.targetOption}`)
      }
    }

    // Build visibility map per declination
    const visibility: Record<string, string[]> = {}
    for (const dec of product.value.declinations) {
      if (!dec.options) continue
      const visibleOpts = dec.options.filter(opt => !hidden.has(`${dec.id}:${opt}`))
      if (visibleOpts.length < dec.options.length) {
        visibility[dec.id] = visibleOpts
      }
    }

    optionVisibility.value = visibility

    // Cascade reset: if a selected option became hidden, reset to first visible.
    // If ALL options are hidden (declination fully restricted), drop the value entirely
    // so it doesn't leak into the price calc, the FP cart, or the quote-line description.
    let changed = false
    for (const dec of product.value.declinations) {
      const selected = configuration.value.declinations[dec.id]
      if (!selected || typeof selected !== 'string' || !dec.options) continue

      const visibleOpts = visibility[dec.id]
      if (visibleOpts && !visibleOpts.includes(selected)) {
        if (visibleOpts.length === 0) {
          delete configuration.value.declinations[dec.id]
        } else {
          configuration.value.declinations[dec.id] = visibleOpts[0]
        }
        changed = true
      }
    }

    // If cascade changed selections, recompute (max 1 extra pass to avoid infinite loops)
    if (changed) {
      const hiddenAfter = new Set<string>()
      for (const dec of product.value.declinations) {
        if (dec.hidden) continue
        const sel = configuration.value.declinations[dec.id]
        if (!sel || typeof sel !== 'string') continue
        const targets = restrictions[`${dec.id}:${sel}`]
        if (!targets) continue
        for (const t of targets) hiddenAfter.add(`${t.targetDecId}:${t.targetOption}`)
      }
      for (const dec of product.value.declinations) {
        if (!dec.options) continue
        const visibleOpts = dec.options.filter(opt => !hiddenAfter.has(`${dec.id}:${opt}`))
        if (visibleOpts.length < dec.options.length) {
          visibility[dec.id] = visibleOpts
        } else {
          delete visibility[dec.id]
        }
      }
      optionVisibility.value = visibility
    }
  }

  // Auto-select hidden broderie tariff tranches based on quantity + emplacement
  function autoSelectBroderieTranches() {
    if (!product.value || !configuration.value) return

    // Find hidden declinations with tranche-pattern options ("1-10 Coeur logo")
    const hiddenDecs = product.value.declinations.filter(d => d.hidden && d.options && d.options.length > 5)
    if (hiddenDecs.length === 0) return

    const qty = configuration.value.quantity || 1

    // Find the emplacement declination (non-hidden, name contains "emplacement")
    const emplacementDec = product.value.declinations.find(d =>
      !d.hidden && d.id.includes('emplacement')
    )
    const emplacement = emplacementDec
      ? (configuration.value.declinations[emplacementDec.id] as string)
      : null

    for (const dec of hiddenDecs) {
      if (!dec.options) continue
      // Parse "MIN-MAX Emplacement" pattern and find matching tranche
      const match = dec.options.find(opt => {
        const m = opt.match(/^(\d+)-(\d+)\s+(.+)$/)
        if (!m) return false
        const [, minStr, maxStr, emp] = m
        const matchesQty = qty >= parseInt(minStr) && qty <= parseInt(maxStr)
        const matchesEmp = !emplacement || emp === emplacement
        return matchesQty && matchesEmp
      })

      if (match) {
        configuration.value.declinations[dec.id] = match
      }
    }
  }

  function updateDeclination(declinationId: string, value: string | string[] | number | unknown[]) {
    if (!configuration.value) return

    configuration.value.declinations[declinationId] = value as string | string[] | number
    computeVisibility()
    autoSelectBroderieTranches()
    recalculatePrice()

    console.log('[ConfiguratorStore] Updated declination:', declinationId, '=', value)
  }

  function updateDimensions(largeur: number, hauteur: number) {
    if (!configuration.value) return

    configuration.value.dimensions = {
      largeur,
      hauteur,
      surface: (largeur * hauteur) / 1000000 // Convert mm² to m²
    }
    recalculatePrice()

    console.log('[ConfiguratorStore] Updated dimensions:', largeur, 'x', hauteur)
  }

  function updateQuantity(qty: number) {
    if (!configuration.value) return

    configuration.value.quantity = Math.max(1, qty)
    autoSelectBroderieTranches()
    recalculatePrice()

    console.log('[ConfiguratorStore] Updated quantity:', qty)
  }

  function recalculatePrice() {
    if (!configuration.value) {
      calculatedPrice.value = null
      return
    }

    calculatedPrice.value = catalogueService.calculatePrice(configuration.value)
    console.log('[ConfiguratorStore] Recalculated price:', calculatedPrice.value?.totalHT)
  }

  function getConfigurationSummary(): string {
    if (!configuration.value) return ''
    return catalogueService.formatDeclinationsSummary(configuration.value)
  }

  // Story 9.3: Setter pour le callback d'ajout au devis
  function setOnAddToQuote(callback: (data: QuoteItemFromConfig) => void) {
    onAddToQuoteCallback.value = callback
    console.log('[ConfiguratorStore] onAddToQuote callback registered')
  }

  // Story 9.3: Action d'ajout au devis
  function addToQuote() {
    if (!product.value || !calculatedPrice.value || !configuration.value || !categoryId.value) {
      console.warn('[ConfiguratorStore] Cannot add to quote - missing data')
      return false
    }

    // Use manual price when prixSurDemande (no automatic pricing)
    const qty = configuration.value.quantity || 1
    const totalHT = calculatedPrice.value.prixSurDemande && manualPrice.value
      ? manualPrice.value
      : calculatedPrice.value.totalHT
    const unitPriceHT = totalHT / qty

    const itemData: QuoteItemFromConfig = {
      productId: product.value.id,
      productName: product.value.name,
      categoryId: categoryId.value,
      description: getConfigurationSummary(),
      quantity: qty,
      unitPrice: Math.round(unitPriceHT * 100) / 100,
      totalPrice: totalHT,
      configuration: { ...configuration.value }
    }

    console.log('[ConfiguratorStore] Adding to quote:', itemData.productName, 'qty:', itemData.quantity)

    // Appeler le callback si défini
    if (onAddToQuoteCallback.value) {
      onAddToQuoteCallback.value(itemData)
    } else {
      console.warn('[ConfiguratorStore] No onAddToQuote callback registered')
    }

    // Fermer et reset
    resetConfigurator()
    return true
  }

  // ============================================
  // Return
  // ============================================

  return {
    // State
    isOpen,
    product,
    categoryId,
    configuration,
    calculatedPrice,
    loading,

    // Getters
    productType,
    breadcrumb,
    categoryName,
    declinationsWithDefaults,
    visibleDeclinations,
    optionVisibility,
    quantity,
    dimensions,

    // Actions
    openConfigurator,
    openForEdit,
    closeConfigurator,
    resetConfigurator,
    updateDeclination,
    updateDimensions,
    updateQuantity,
    recalculatePrice,
    getConfigurationSummary,
    // Manual price for prixSurDemande products
    manualPrice,
    // Edit mode
    isEditMode,
    setManualPrice,
    // Story 9.3
    setOnAddToQuote,
    addToQuote
  }
})
